import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { FaCircleInfo } from 'react-icons/fa6';
import { useShopState } from '../../hooks/useShopState';
import { useLogin } from '../../hooks/useLogin';
import { isAdmin } from '../../auth/roles';
import './ShopStateNotice.scss';

export const SHOP_STATE_SWITCH_PATH = '/admin/settings/shop-state';

interface ShopStateNoticeProps {
  /** In the page content (in place of a blocked page or action) rather than
   *  as the slim strip above the top nav. */
  inline?: boolean;
}

/**
 * What the shop state means for the viewer, when it means anything:
 * - staff of a shop that is not open: a calm reminder that customers are
 *   held back, with a link to the switch for the admins who may flip it;
 * - customers (and visitors) of a paused shop: that nothing can be ordered
 *   now, and what still works.
 * Renders nothing otherwise — a customer of a closed shop gets
 * ShopClosedPage from MainLayout instead.
 */
const ShopStateNotice = ({ inline = false }: ShopStateNoticeProps) => {
  const { t } = useTranslation();
  const { state, isStaffView, pausedForMe } = useShopState();
  const { userData } = useLogin();

  let message: string | null = null;
  if (isStaffView && state === 'closed') message = t('shopState.staff_closed');
  else if (isStaffView && state === 'orders_paused') message = t('shopState.staff_paused');
  else if (pausedForMe) message = t('shopState.customer_paused');
  if (!message) return null;

  // Only admins may switch; an impersonating admin carries customer roles.
  const canSwitch = isStaffView && isAdmin(userData?.roles);

  return (
    <div
      className={`shop-state-notice notification is-info is-light${inline ? ' shop-state-notice--inline' : ''}`}
      role="status"
      data-testid="shop-state-notice"
    >
      <span className="shop-state-notice__label">
        <FaCircleInfo aria-hidden="true" />
        <span>{message}</span>
      </span>
      {canSwitch && (
        <Link className="shop-state-notice__link" to={SHOP_STATE_SWITCH_PATH}>
          {t('shopState.change')}
        </Link>
      )}
    </div>
  );
};

export default ShopStateNotice;
